import {
  ApiOutlined,
  CalendarOutlined,
  CloudServerOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  LockOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Card, Result } from 'antd';
import { useTranslation } from 'react-i18next';

import { TiendaPerfectaRouteReportContext } from 'models/TiendaPerfecta';

import { RouteReportBlockedReason } from '../types';
import { formatInstant, formatLongDate, getUserTimeZone } from '../utils/time';

import './RouteReportBlockedState.scss';

const I18N_BASE = 'scenes.route_report.components.blocked_state';

const ICON_BY_REASON: Record<RouteReportBlockedReason, JSX.Element> = {
  integration_disabled: <ApiOutlined />,
  company_required: <ExclamationCircleOutlined />,
  scope_not_configured: <LockOutlined />,
  no_distributors: <InboxOutlined />,
  generic: <ExclamationCircleOutlined />,
};

interface RouteReportBlockedStateProps {
  reason: RouteReportBlockedReason;
}

/**
 * Cada motivo se arregla en un lugar distinto —dos en Compañías, uno en Roles—,
 * así que cada uno dice qué pasa y dónde resolverlo en vez de un vacío genérico.
 */
function RouteReportBlockedState({ reason }: RouteReportBlockedStateProps) {
  const { t } = useTranslation();

  return (
    <Card>
      <Result
        icon={
          <span className="route-report-blocked__icon">
            {ICON_BY_REASON[reason]}
          </span>
        }
        title={t(`${I18N_BASE}.${reason}.title`)}
        subTitle={
          <span className="route-report-blocked__subtitle">
            <span>{t(`${I18N_BASE}.${reason}.description`)}</span>
            <span className="route-report-blocked__hint">
              {t(`${I18N_BASE}.${reason}.hint`)}
            </span>
          </span>
        }
      />
    </Card>
  );
}

interface RouteReportEmptyDayProps {
  /** El contexto del día que contestó el servidor. */
  context: TiendaPerfectaRouteReportContext | null;
}

/** Un día sin plan no es un error: es un `200` con `routes: []`. */
export function RouteReportEmptyDay({ context }: RouteReportEmptyDayProps) {
  const { t } = useTranslation();

  /**
   * El día por defecto sale del reloj del navegador, pero el reporte es un día
   * de la COMPAÑÍA. Desde una zona por delante se abre un día que allá todavía
   * no empezó y el vacío parece un problema de datos.
   *
   * La comparación es entre los dos RELOJES —el del usuario y el de la
   * compañía—, no contra la fecha consultada: elegir a mano un día pasado sin
   * plan es un vacío legítimo y explicarlo con la zona horaria sería confundir.
   * La línea aparece solo cuando el desfase existe de verdad Y el usuario está
   * mirando su propio hoy.
   */
  const now = new Date();
  const companyToday = context
    ? formatInstant(now, context.timezone, 'YYYY-MM-DD')
    : null;
  const userToday = formatInstant(now, getUserTimeZone(), 'YYYY-MM-DD');
  const otherCalendarDay =
    companyToday !== null &&
    companyToday !== userToday &&
    context?.date === userToday;

  return (
    <Card>
      <Result
        icon={
          <span className="route-report-blocked__icon">
            <CalendarOutlined />
          </span>
        }
        title={t('scenes.route_report.scene.empty.title')}
        subTitle={
          <span className="route-report-blocked__subtitle">
            <span>{t('scenes.route_report.scene.empty.description')}</span>
            {otherCalendarDay && companyToday && (
              <span>
                {t('scenes.route_report.scene.empty.company_today', {
                  date: formatLongDate(companyToday),
                })}
              </span>
            )}
            <span className="route-report-blocked__hint">
              {t('scenes.route_report.scene.empty.hint')}
            </span>
          </span>
        }
      />
    </Card>
  );
}

interface RouteReportErrorStateProps {
  onRetry: () => void;
}

/**
 * Una carga que falló y dejó la pantalla sin nada.
 *
 * El toast dura 3.5 segundos y después no queda ninguna señal: sin esto la
 * pantalla se quedaba con los skeletons puestos para siempre, como si todavía
 * estuviera cargando. Se puede reintentar sin salir de la vista.
 */
export function RouteReportErrorState({ onRetry }: RouteReportErrorStateProps) {
  const { t } = useTranslation();

  return (
    <Card>
      <Result
        icon={
          <span className="route-report-blocked__icon">
            <CloudServerOutlined />
          </span>
        }
        title={t('scenes.route_report.scene.error.title')}
        subTitle={
          <span className="route-report-blocked__subtitle">
            <span>{t('scenes.route_report.scene.error.description')}</span>
            <span className="route-report-blocked__hint">
              {t('scenes.route_report.scene.error.hint')}
            </span>
          </span>
        }
        extra={
          <Button type="primary" icon={<ReloadOutlined />} onClick={onRetry}>
            {t('scenes.route_report.scene.error.retry')}
          </Button>
        }
      />
    </Card>
  );
}

export default RouteReportBlockedState;
